import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Bell, CalendarClock, ChevronRight } from "lucide-react";
import api from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import AgendaQuickAdd from "@/components/rhythm/AgendaQuickAdd";

/**
 * Dashboard-only widget: "Your Next 1:1" for staff/volunteers with pairings.
 * Dual-hat aware — a manager who is ALSO an employee sees THEIR OWN pairing
 * first ("Your Own Development"), with the team 1:1 count shown as a warm
 * secondary line. A dropdown lets them retarget the quick-add to any of
 * their pairings without leaving the dashboard.
 */
export default function NextOneOnOneCard() {
  const { user } = useAuth();
  const [pairings, setPairings] = useState([]);
  const [loaded, setLoaded] = useState(false);
  const [selectedId, setSelectedId] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/rhythm/pairings");
        setPairings(data);
      } catch {
        /* silently ignore — volunteers 403; nothing to show */
      } finally {
        setLoaded(true);
      }
    })();
  }, []);

  const { mine, team, sorted, active, isViewerEmployee, other, others, teamCount } = useMemo(() => {
    const bySoonest = (a, b) => {
      const ad = a.next_scheduled || "9999-12-31";
      const bd = b.next_scheduled || "9999-12-31";
      return ad.localeCompare(bd);
    };
    const mineList = pairings.filter((p) => String(p.employee_id) === String(user?.id));
    const teamList = pairings.filter((p) => String(p.supervisor_id) === String(user?.id));
    // Combine mine + team for the retargeting dropdown. Mine appears FIRST
    // (viewer's own development is the primary hat), then team by soonest.
    const mineSorted = [...mineList].sort(bySoonest);
    const teamSorted = [...teamList].sort(bySoonest);
    const sortedList = [...mineSorted, ...teamSorted];
    const first = sortedList[0] || null;
    const current = sortedList.find((p) => p.id === selectedId) || first;
    const viewerIsEmployee = current
      ? String(current.employee_id) === String(user?.id)
      : mineList.length > 0;
    const otherName = current
      ? (viewerIsEmployee ? current.supervisor_name : current.employee_name)
      : "";
    const otherPairings = current ? sortedList.filter((p) => p.id !== current.id) : [];
    return {
      mine: mineList,
      team: teamList,
      sorted: sortedList,
      active: current,
      isViewerEmployee: viewerIsEmployee,
      other: otherName,
      others: otherPairings,
      teamCount: teamList.length,
    };
  }, [pairings, user, selectedId]);

  if (!loaded) return null;
  if (!active) return null;

  return (
    <section
      className="patria-card p-6"
      data-testid="dashboard-next-one-on-one"
    >
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="flex items-start gap-3">
          <div className="h-11 w-11 rounded-full bg-forest-900/10 text-forest-900 flex items-center justify-center shrink-0">
            <CalendarClock className="h-5 w-5" strokeWidth={1.5} />
          </div>
          <div>
            <p className="text-[10px] uppercase tracking-widest text-terracotta-500 font-medium">
              RHYTHM · {isViewerEmployee ? "Your Next 1:1 · Your Own Development" : "Your Next 1:1 · Team"}
            </p>
            <p className="font-serif text-2xl text-forest-900 mt-1">
              {other || "Your Teammate"}
            </p>
            <p className="text-xs text-charcoal-muted">
              {active.next_scheduled ? `On ${active.next_scheduled}` : "Cadence pending"}
              {" · "}
              {(active.effective_cadence || "").charAt(0).toUpperCase() + (active.effective_cadence || "").slice(1)}
              {active.compass_override && " · COMPASS Override"}
            </p>
            {isViewerEmployee && teamCount > 0 && (
              <p className="text-[11px] text-forest-700 mt-1" data-testid="dashboard-team-count-line">
                Also shepherding {teamCount} team 1:1{teamCount === 1 ? "" : "s"} — see them below.
              </p>
            )}
          </div>
        </div>
        {active.unread_agenda_from_other > 0 && (
          <span
            className="patria-chip-terra text-[10px]"
            data-testid="dashboard-rhythm-unread"
            title={`${active.unread_agenda_from_other} new agenda topic(s) from ${other}`}
          >
            <Bell className="h-3 w-3 mr-1" strokeWidth={1.5} />
            {active.unread_agenda_from_other} New
          </span>
        )}
      </div>

      {sorted.length > 1 && (
        <div className="mt-3">
          <label className="patria-label text-[10px]">Add A Topic To</label>
          <select
            className="patria-input w-full text-sm"
            value={active.id}
            onChange={(e) => setSelectedId(e.target.value)}
            data-testid="dashboard-quick-add-target-select"
          >
            {sorted.map((p) => {
              const label = String(p.employee_id) === String(user?.id)
                ? `${p.supervisor_name} (My 1:1)`
                : `${p.employee_name} (Team)`;
              return <option key={p.id} value={p.id}>{label}</option>;
            })}
          </select>
        </div>
      )}

      <div className="mt-4">
        <AgendaQuickAdd
          pairingId={active.id}
          onAdded={() => { /* no-op — server owns state */ }}
          suggestions
          placeholder="Add A Topic For Your Next 1:1…"
          testIdPrefix="dashboard-quick-add"
        />
      </div>

      <div className="mt-4 flex flex-wrap items-center gap-3">
        <Link
          to={`/rhythm/workspace/${active.id}`}
          className="text-sm text-forest-700 font-medium inline-flex items-center gap-1"
          data-testid="dashboard-open-next-workspace"
        >
          Open Workspace <ChevronRight className="h-4 w-4" strokeWidth={1.5} />
        </Link>
        {isViewerEmployee && (
          <Link
            to={`/rhythm/growth/${user?.id}`}
            className="text-xs text-forest-700 patria-link"
            data-testid="dashboard-my-growth-plan"
          >
            My Growth Plan →
          </Link>
        )}
        {others.length > 0 && (
          <Link
            to="/rhythm"
            className="text-xs text-charcoal-muted patria-link"
            data-testid="dashboard-see-all-pairings"
          >
            + {others.length} other pairing{others.length === 1 ? "" : "s"}
          </Link>
        )}
      </div>
    </section>
  );
}
